import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    MessageCircle, Send, Loader2, Search, Clock, Check, CheckCheck,
    AlertCircle, User, Phone, ChevronLeft, Settings,
} from 'lucide-react';
import {
    useWhatsAppStatus, useWhatsAppConversations,
    useWhatsAppMessages, useSendWhatsAppMessage,
} from '../hooks/useWhatsApp';

/* ── Delivery Ticks ──────────────────────────────────────────────────────── */
function DeliveryStatus({ status }) {
    switch (status) {
        case 'sent':
            return <Check className="w-3 h-3 text-stone-400" />;
        case 'delivered':
            return <CheckCheck className="w-3 h-3 text-stone-400" />;
        case 'read':
            return <CheckCheck className="w-3 h-3 text-blue-500" />;
        case 'failed':
            return <AlertCircle className="w-3 h-3 text-red-500" />;
        default:
            return <Clock className="w-3 h-3 text-stone-300" />;
    }
}

/* ── Service Window Badge ────────────────────────────────────────────────── */
function ServiceWindowBadge({ isOpen }) {
    if (isOpen) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Window open
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3" />
            Template only
        </span>
    );
}

/* ── Message Bubble ──────────────────────────────────────────────────────── */
function MessageBubble({ message }) {
    const isOutbound = message.direction === 'outbound';
    const body = message.content?.body || message.content?.caption || `[${message.message_type}]`;
    const time = message.timestamp
        ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}>
            <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                isOutbound
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white border border-stone-200 text-stone-800 rounded-bl-md'
            }`}>
                <p className="text-sm whitespace-pre-wrap break-words">{body}</p>
                <div className={`flex items-center justify-end gap-1 mt-1 ${
                    isOutbound ? 'text-emerald-200' : 'text-stone-400'
                }`}>
                    <span className="text-[10px]">{time}</span>
                    {isOutbound && <DeliveryStatus status={message.status} />}
                </div>
            </div>
        </div>
    );
}

/* ── Conversation List Item ──────────────────────────────────────────────── */
function ConversationItem({ conversation, isActive, onClick }) {
    const lastMsg = conversation.last_message;
    const body = lastMsg?.content?.body || `[${lastMsg?.message_type || 'message'}]`;
    const time = lastMsg?.timestamp
        ? new Date(lastMsg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
    const name = conversation.contact_name || conversation.contact_phone || 'Unknown';

    return (
        <button
            onClick={onClick}
            className={`w-full text-left p-3 border-b border-stone-100 hover:bg-stone-50 transition-colors ${
                isActive ? 'bg-emerald-50 border-l-2 border-l-emerald-500' : ''
            }`}
        >
            <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-medium"
                    style={{ backgroundColor: '#E8FFF0', color: '#25D366' }}>
                    {name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-stone-900 truncate">{name}</span>
                        <span className="text-[10px] text-stone-400 shrink-0 ml-2">{time}</span>
                    </div>
                    <p className="text-xs text-stone-500 truncate mt-0.5">
                        {lastMsg?.direction === 'outbound' && <span className="text-stone-400">You: </span>}
                        {body}
                    </p>
                </div>
                {conversation.unread_count > 0 && (
                    <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0"
                        style={{ backgroundColor: '#25D366', color: 'white' }}>
                        {conversation.unread_count}
                    </span>
                )}
            </div>
        </button>
    );
}

/* ── Composer ─────────────────────────────────────────────────────────────── */
function WhatsAppComposer({ contactId, serviceWindowOpen }) {
    const [text, setText] = useState('');
    const sendMessage = useSendWhatsAppMessage();
    const inputRef = useRef(null);

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed || !contactId) return;

        sendMessage.mutate(
            { contactId, body: trimmed },
            {
                onSuccess: () => {
                    setText('');
                    inputRef.current?.focus();
                },
                onError: (err) => {
                    const detail = err.response?.data?.detail || 'Failed to send message';
                    toast.error(typeof detail === 'string' ? detail : 'Failed to send');
                },
            }
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!serviceWindowOpen) {
        return (
            <div className="p-3 border-t border-stone-200 bg-amber-50">
                <div className="flex items-center gap-2 text-xs text-amber-700">
                    <Clock className="w-4 h-4 shrink-0" />
                    <span>24-hour service window expired. Send a template message to re-initiate conversation.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 border-t border-stone-200 bg-white">
            <div className="flex items-end gap-2">
                <textarea
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 max-h-32"
                    style={{ minHeight: '40px' }}
                />
                <button
                    onClick={handleSend}
                    disabled={!text.trim() || sendMessage.isPending}
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-40"
                    style={{ backgroundColor: '#25D366', color: 'white' }}
                >
                    {sendMessage.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Send className="w-4 h-4" />
                    )}
                </button>
            </div>
        </div>
    );
}

/* ── Thread View ─────────────────────────────────────────────────────────── */
function WhatsAppThread({ contactId, contactName, contactPhone, serviceWindowOpen, onBack }) {
    const { data: messages = [], isLoading } = useWhatsAppMessages(contactId);
    const endRef = useRef(null);
    const navigate = useNavigate();

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 bg-white">
                <button onClick={onBack} className="md:hidden p-1 rounded-lg hover:bg-stone-100">
                    <ChevronLeft className="w-5 h-5 text-stone-600" />
                </button>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium"
                    style={{ backgroundColor: '#E8FFF0', color: '#25D366' }}>
                    {(contactName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-stone-900 truncate">{contactName || 'Unknown'}</span>
                        <ServiceWindowBadge isOpen={serviceWindowOpen} />
                    </div>
                    {contactPhone && <p className="text-xs text-stone-400">{contactPhone}</p>}
                </div>
                {contactId && (
                    <button
                        onClick={() => navigate(`/contacts/${contactId}`)}
                        className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500"
                        title="View contact"
                    >
                        <User className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3" style={{ backgroundColor: '#ECE5DD' }}>
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-stone-400">
                        <MessageCircle className="w-10 h-10 mb-2 opacity-30" />
                        <p className="text-sm">No messages yet</p>
                        <p className="text-xs mt-1">Send a message to start the conversation</p>
                    </div>
                ) : (
                    <>
                        {messages.map((msg) => (
                            <MessageBubble key={msg.message_id || msg.wa_message_id} message={msg} />
                        ))}
                        <div ref={endRef} />
                    </>
                )}
            </div>

            {/* Composer */}
            <WhatsAppComposer contactId={contactId} serviceWindowOpen={serviceWindowOpen} />
        </div>
    );
}

/* ── Main WhatsApp Chat Component ────────────────────────────────────────── */
export default function WhatsAppChat() {
    const navigate = useNavigate();
    const { data: status } = useWhatsAppStatus();
    const { data: conversations = [], isLoading: loadingConvos } = useWhatsAppConversations(status?.connected);

    const [activeContact, setActiveContact] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredConvos = conversations.filter((c) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            (c.contact_name || '').toLowerCase().includes(q) ||
            (c.contact_phone || '').toLowerCase().includes(q)
        );
    });

    const activeConvo = conversations.find((c) => c.contact_id === activeContact);

    // Not connected state
    if (!status?.connected) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: '#E8FFF0' }}>
                    <MessageCircle className="w-8 h-8" style={{ color: '#25D366' }} />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-1">Connect WhatsApp</h3>
                <p className="text-sm text-stone-500 mb-4 max-w-sm">
                    Connect your WhatsApp Business account to send and receive messages directly from your CRM.
                </p>
                <button
                    onClick={() => navigate('/settings?tab=integrations')}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: '#25D366' }}
                >
                    <Settings className="w-4 h-4" /> Connect in Settings
                </button>
            </div>
        );
    }

    return (
        <div className="flex h-full border border-stone-200 rounded-xl overflow-hidden bg-white">
            {/* Conversation List (left panel) */}
            <div className={`w-80 border-r border-stone-200 flex flex-col shrink-0 ${
                activeContact ? 'hidden md:flex' : 'flex'
            }`}>
                {/* Search */}
                <div className="p-3 border-b border-stone-100">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search conversations..."
                            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                        />
                    </div>
                </div>

                {/* Conversations */}
                <div className="flex-1 overflow-y-auto">
                    {loadingConvos ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                        </div>
                    ) : filteredConvos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-stone-400">
                            <MessageCircle className="w-8 h-8 mb-2 opacity-30" />
                            <p className="text-xs">No conversations yet</p>
                        </div>
                    ) : (
                        filteredConvos.map((conv) => (
                            <ConversationItem
                                key={conv.contact_id}
                                conversation={conv}
                                isActive={conv.contact_id === activeContact}
                                onClick={() => setActiveContact(conv.contact_id)}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Thread (right panel) */}
            <div className={`flex-1 flex flex-col ${
                !activeContact ? 'hidden md:flex' : 'flex'
            }`}>
                {activeContact ? (
                    <WhatsAppThread
                        contactId={activeContact}
                        contactName={activeConvo?.contact_name}
                        contactPhone={activeConvo?.contact_phone}
                        serviceWindowOpen={activeConvo?.service_window_open ?? false}
                        onBack={() => setActiveContact(null)}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-stone-400">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
                            style={{ backgroundColor: '#E8FFF0' }}>
                            <MessageCircle className="w-8 h-8" style={{ color: '#25D366' }} />
                        </div>
                        <p className="text-sm font-medium text-stone-500">Select a conversation</p>
                        <p className="text-xs text-stone-400 mt-1">Choose from your WhatsApp conversations</p>
                    </div>
                )}
            </div>
        </div>
    );
}
